/**
 * Central de Projetos: Assistente de Projetos (chat), com as conversas salvas.
 *
 * Pergunta-se em português sobre clientes, grupos, prazos e pendências. O
 * Jev escolhe as ferramentas da Central (as mesmas do MCP), o sistema as roda
 * e o Gemini responde com os resultados num pedido só. Só consulta: não envia
 * mensagem e não muda nada.
 *
 * - Conversas salvas na coluna da esquerda, como num chat: cada um vê só as
 *   suas. Retomar uma conversa continua com o contexto dela (o histórico que
 *   vai ao Gemini sai do servidor).
 * - Memória do cliente: quando a pergunta cita um cliente, o servidor junta o
 *   que já foi respondido sobre ele nas outras conversas da mesma pessoa
 *   (fonte "conversas anteriores"). Nada passa entre pessoas.
 * - Cada resposta tem Copiar e Responder (a pergunta seguinte vai citando).
 * - Rotas: `/central-projetos/assistente` (no computador, já a conversa
 *   nova; no celular, a lista), `/assistente/nova` e `/assistente/:id`.
 * - ESC sai da conversa aberta, como nas Conversas: antes tira a citação, e
 *   com texto no campo não faz nada (não perde o que foi escrito).
 *
 * Cada pergunta gasta 1 dos pedidos do dia ao Gemini: o rodapé mostra
 * quantos sobram. O erro é o `detalhe` do servidor, como veio.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiArrowLeft } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";

import ConfirmDialog from "../../components/ConfirmDialog";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import useMediaQuery from "../../hooks/useMediaQuery";
import {
  apagarConversaDoAssistente,
  listarConversasDoAssistente,
  renomearConversaDoAssistente,
} from "../../services/centralProjetos";
import { plural } from "../../utils/dinheiro";
import Escrita from "./assistente/Escrita";
import ListaDoAssistente from "./assistente/ListaDoAssistente";
import Turnos, { ComecoDaConversa } from "./assistente/Turnos";
import useConversaDoAssistente from "./assistente/useConversaDoAssistente";
import { ErroDaCentral, useCarga } from "./comum";
import {
  AreaDasMensagens,
  BotaoDeTexto,
  BotaoIcone,
  Cabecalho,
  CELULAR,
  ColunaConversa,
  IdentidadeDaConversa,
  Moldura,
  NotaCentral,
  Tela,
} from "./conversas/estilos";
import useEscParaSair from "./conversas/useEscParaSair";
import { perguntasRestantes } from "./gemini";
import useGeminiHoje from "./useGeminiHoje";

const BASE = "/central-projetos/assistente";
const NOVA = "nova";
const SUBTITULO = "Consulta a planilha, o WhatsApp e as análises. Não envia nada.";
/** Do limite da unidade (quando o Gemini não informa o dele). */
const POUCAS_RESTANTES = 5;
/** Da fatia do Assistente no Gemini: avisa antes de acabar. */
const POUCAS_NO_GEMINI = 2;
const NAO_ENCONTRADA = 404;

function mesmoId(a, b) {
  return a !== undefined && a !== null && b !== undefined && b !== null && String(a) === String(b);
}

/** Depois de sair da conversa, o foco volta à linha dela (quem usa teclado continua de onde estava). */
function focarNaLista(id) {
  window.requestAnimationFrame(() => {
    document.querySelector(`[data-conversa-assistente-id="${String(id).replace(/"/g, "")}"]`)?.focus();
  });
}

function contadorDoDia(gemini, restantes) {
  const noGemini = perguntasRestantes(gemini);
  if (noGemini) return { texto: noGemini.texto, acabando: noGemini.restam <= POUCAS_NO_GEMINI };
  if (restantes === null) return null;
  return {
    texto: plural(restantes, "pergunta restante hoje", "perguntas restantes hoje"),
    acabando: restantes <= POUCAS_RESTANTES,
  };
}

function ConteudoDaConversa({ conversa, enviando, falha, aoPerguntar, aoTentarDeNovo, aoResponder, aoNova }) {
  if (conversa.carregando) {
    return <NotaCentral aria-busy="true">Carregando a conversa…</NotaCentral>;
  }
  if (conversa.erro) {
    if (conversa.erro.status !== NAO_ENCONTRADA) return <div style={{ padding: 16 }}><ErroDaCentral erro={conversa.erro} /></div>;
    return (
      <NotaCentral>
        Esta conversa não existe mais: foi apagada ou é de outra pessoa.{" "}
        <BotaoDeTexto type="button" onClick={aoNova}>Começar uma nova</BotaoDeTexto>
      </NotaCentral>
    );
  }
  if (conversa.turnos.length === 0 && !enviando) {
    return <ComecoDaConversa aoPerguntar={aoPerguntar} desabilitado={enviando} />;
  }
  return (
    <Turnos turnos={conversa.turnos} enviando={enviando} falha={falha} aoTentarDeNovo={aoTentarDeNovo} aoResponder={aoResponder} />
  );
}

export default function AssistenteCentral() {
  const { conversaId: parametro } = useParams();
  const navigate = useNavigate();
  const { request } = useAuth();
  const toast = useToast();
  const celular = useMediaQuery(CELULAR);
  const conversaId = parametro && parametro !== NOVA ? parametro : null;
  const naNova = parametro === NOVA;

  const lista = useCarga((req) => listarConversasDoAssistente(req), []);
  const { alterar: alterarLista } = lista;
  const conversas = lista.dados;

  const criadaAgora = useRef(null);
  const porNoTopo = useCallback(
    (salva) => alterarLista((atuais) => [salva, ...atuais.filter((c) => !mesmoId(c.id, salva.id))]),
    [alterarLista],
  );
  const aoCriar = useCallback(
    (salva) => {
      criadaAgora.current = String(salva.id);
      navigate(`${BASE}/${salva.id}`, { replace: true });
    },
    [navigate],
  );
  const { conversa, enviando, falha, perguntar, tentarDeNovo, recomecar } = useConversaDoAssistente(conversaId, {
    aoSalvar: porNoTopo,
    aoCriar,
  });

  const [pergunta, setPergunta] = useState("");
  const [citando, setCitando] = useState(null);
  const [apagando, setApagando] = useState(null);
  const [restantes, setRestantes] = useState(null);
  const campo = useRef(null);
  const rolagem = useRef(null);
  const { gemini, recarregar: relerCota, absorver: absorverCota } = useGeminiHoje();

  // Trocou de conversa: a citação e o texto não vão junto. A que acabou de
  // ser criada é a mesma conversa (só ganhou id): o que está sendo escrito fica.
  useEffect(() => {
    if (conversaId && criadaAgora.current === String(conversaId)) return;
    setCitando(null);
    setPergunta("");
  }, [conversaId]);

  useEffect(() => {
    const el = rolagem.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [conversa.turnos, enviando, falha]);

  const absorverResposta = useCallback(
    (resposta) => {
      if (resposta && typeof resposta.restantes === "number") setRestantes(resposta.restantes);
      if (resposta?.gemini_hoje) absorverCota(resposta.gemini_hoje);
      else relerCota();
      campo.current?.focus();
    },
    [absorverCota, relerCota],
  );

  function enviarPergunta(texto) {
    const limpa = String(texto ?? pergunta).trim();
    if (!limpa || enviando || conversa.carregando || conversa.erro) return;
    const citada = citando;
    setPergunta("");
    setCitando(null);
    perguntar(limpa, citada).then(absorverResposta);
  }

  function responderA(turno) {
    setCitando({ id: turno.id, texto: turno.texto });
    campo.current?.focus();
  }

  function novaConversa() {
    if (!conversaId) {
      recomecar();
      setPergunta("");
      setCitando(null);
    }
    if (!naNova) navigate(`${BASE}/${NOVA}`);
    window.requestAnimationFrame(() => campo.current?.focus());
  }

  async function renomear(item, titulo) {
    const anterior = item.titulo;
    alterarLista((atuais) => atuais.map((c) => (mesmoId(c.id, item.id) ? { ...c, titulo } : c)));
    try {
      const salva = await renomearConversaDoAssistente(request, item.id, titulo);
      alterarLista((atuais) => atuais.map((c) => (mesmoId(c.id, item.id) ? { ...c, titulo: salva?.titulo || titulo } : c)));
    } catch {
      // O `request` já mostrou o erro do servidor; o nome antigo volta.
      alterarLista((atuais) => atuais.map((c) => (mesmoId(c.id, item.id) ? { ...c, titulo: anterior } : c)));
    }
  }

  async function apagar() {
    const alvo = apagando;
    await apagarConversaDoAssistente(request, alvo.id);
    alterarLista((atuais) => atuais.filter((c) => !mesmoId(c.id, alvo.id)));
    setApagando(null);
    toast.success("Conversa apagada.");
    if (mesmoId(conversaId, alvo.id)) navigate(celular ? BASE : `${BASE}/${NOVA}`, { replace: true });
  }

  const sair = useCallback(() => {
    if (campo.current?.value.trim()) return;
    const id = conversaId;
    navigate(celular ? BASE : `${BASE}/${NOVA}`);
    focarNaLista(id);
  }, [celular, conversaId, navigate]);

  useEscParaSair({
    ativo: Boolean(conversaId) && !apagando,
    citando: Boolean(citando),
    aoCancelarCitacao: () => setCitando(null),
    comAudio: false,
    aoConfirmarSaida: sair,
    aoSair: sair,
  });

  const itemAberto = conversaId ? (conversas || []).find((c) => mesmoId(c.id, conversaId)) : null;
  const titulo = conversaId ? itemAberto?.titulo || conversa.titulo || "Conversa" : "Nova conversa";
  // No celular é uma coisa de cada vez: a lista, ou a conversa (com voltar).
  const abertaNoCelular = Boolean(conversaId) || naNova;
  const listaEscondida = celular && abertaNoCelular;
  const mostrarConversa = !celular || abertaNoCelular;

  return (
    <Tela>
      <Moldura>
        <ListaDoAssistente
          conversas={conversas}
          carregando={lista.carregando}
          erro={lista.erro}
          ativaId={conversaId}
          escondida={listaEscondida}
          aoVoltar={() => navigate("/central-projetos")}
          aoNova={novaConversa}
          aoAbrir={(c) => navigate(`${BASE}/${c.id}`)}
          aoRenomear={renomear}
          aoApagar={setApagando}
        />
        {mostrarConversa ? (
          <ColunaConversa aria-label="Conversa com o assistente">
            <Cabecalho>
              {celular ? (
                <BotaoIcone type="button" aria-label="Voltar às conversas" title="Voltar" onClick={() => navigate(BASE)}>
                  <FiArrowLeft size={18} aria-hidden="true" />
                </BotaoIcone>
              ) : null}
              <IdentidadeDaConversa>
                <h2 title={titulo}>{titulo}</h2>
                <p>{SUBTITULO}</p>
              </IdentidadeDaConversa>
            </Cabecalho>

            <AreaDasMensagens ref={rolagem} aria-live="polite">
              <ConteudoDaConversa
                conversa={conversa}
                enviando={enviando}
                falha={falha}
                aoPerguntar={enviarPergunta}
                aoTentarDeNovo={() => tentarDeNovo().then(absorverResposta)}
                aoResponder={responderA}
                aoNova={novaConversa}
              />
            </AreaDasMensagens>

            {conversa.erro ? null : (
              <Escrita
                campo={campo}
                pergunta={pergunta}
                aoMudar={setPergunta}
                aoPerguntar={() => enviarPergunta()}
                enviando={enviando}
                citando={citando}
                aoCancelarCitacao={() => {
                  setCitando(null);
                  campo.current?.focus();
                }}
                contador={contadorDoDia(gemini, restantes)}
              />
            )}
          </ColunaConversa>
        ) : null}
      </Moldura>

      <ConfirmDialog
        open={Boolean(apagando)}
        titulo="Apagar esta conversa?"
        descricao={`"${apagando?.titulo || "Conversa sem título"}" sai da sua lista e o assistente deixa de lembrar dela. Não dá para desfazer.`}
        confirmarLabel="Apagar"
        tone="danger"
        onConfirmar={apagar}
        onCancelar={() => setApagando(null)}
      />
    </Tela>
  );
}
